/**
 * Utilitaires pour extraire et formater les données des champs d'items touristiques
 */

import { TranslationService } from "../services/TranslationService";
import { isSameCalendarDay } from "./openingUtils";
import {
  ObjetTouristique,
  PeriodeOuverture,
  ElementReference,
  LibelleMultilingue,
  MoyenCommunicationType,
} from "../types/apidae.d";
import { WidgetLanguage, DisplayedFieldPreviewId } from "../types/widgets.d";

/**
 * Paramètres nécessaires pour les fonctions utilitaires
 */
export interface ItemFieldUtilsParams {
  language: WidgetLanguage;
  getLabel: (libelle: any) => string;
  formatDate: (dateStr: string, options?: Intl.DateTimeFormatOptions) => string;
  t: (key: string) => string;
}

/**
 * Récupère l'ElementReference du type depuis les structures d'informations d'un item
 */
export function getTypeElementReference(
  item: ObjetTouristique,
): ElementReference | null {
  // Récupérer l'ElementReference du type selon le type d'objet
  switch (item.type) {
    case "ACTIVITE":
      return item.informationsActivite?.activiteType || null;
    case "COMMERCE_ET_SERVICE":
      return item.informationsCommerceEtService?.commerceEtServiceType || null;
    case "DOMAINE_SKIABLE":
      return item.informationsDomaineSkiable?.classification || null;
    case "FETE_ET_MANIFESTATION":
      // Prendre le premier type de manifestation
      return (
        item.informationsFeteEtManifestation?.typesManifestation?.[0] || null
      );
    case "EQUIPEMENT":
      return item.informationsEquipement?.rubrique || null;
    case "HEBERGEMENT_COLLECTIF":
      return (
        item.informationsHebergementCollectif?.hebergementCollectifType || null
      );
    case "HEBERGEMENT_LOCATIF":
      return (
        item.informationsHebergementLocatif?.hebergementLocatifType || null
      );
    case "HOTELLERIE_PLEIN_AIR":
      return (
        item.informationsHotelleriePleinAir?.hotelleriePleinAirType || null
      );
    case "HOTELLERIE":
      return item.informationsHotellerie?.hotellerieType || null;
    case "PATRIMOINE_CULTUREL":
      return (
        item.informationsPatrimoineCulturel?.patrimoineCulturelType || null
      );
    case "PATRIMOINE_NATUREL":
      // Prendre la première catégorie
      return item.informationsPatrimoineNaturel?.categories?.[0] || null;
    case "DEGUSTATION":
      // Prendre le premier type de produit
      return item.informationsDegustation?.typesProduit?.[0] || null;
    case "RESTAURATION":
      return item.informationsRestauration?.restaurationType || null;
    case "SEJOUR_PACKAGE":
      // Prendre la première activité culturelle ou sportive
      return (
        item.informationsSejourPackage?.activitesCulturelles?.[0] ||
        item.informationsSejourPackage?.activitesSportives?.[0] ||
        null
      );
    default:
      return null;
  }
}

/**
 * Extrait la valeur des coordonnées selon la langue
 */
export function getCoordonneesValue(
  coordonnees: {
    fr?: string;
    en?: string;
    es?: string;
    it?: string;
    de?: string;
    nl?: string;
    ru?: string;
    zh?: string;
    ptBR?: string;
    ja?: string;
    ko?: string;
  },
  language: WidgetLanguage,
): string | null {
  if (!coordonnees) return null;

  // Mapper la langue du widget vers la clé de coordonnées
  const langMap: Record<string, keyof typeof coordonnees> = {
    [WidgetLanguage.FR]: "fr",
    [WidgetLanguage.EN]: "en",
    [WidgetLanguage.ES]: "es",
    [WidgetLanguage.IT]: "it",
    [WidgetLanguage.DE]: "de",
    [WidgetLanguage.NL]: "nl",
    [WidgetLanguage.RU]: "ru",
    [WidgetLanguage.ZH]: "zh",
    [WidgetLanguage.PT_BR]: "ptBR",
    [WidgetLanguage.JA]: "ja",
    [WidgetLanguage.KO]: "ko",
  };

  const key = langMap[language];
  if (key && coordonnees[key]) {
    return coordonnees[key] || null;
  }

  // Fallback sur français si disponible
  if (coordonnees.fr) {
    return coordonnees.fr;
  }

  // Sinon, prendre la première valeur disponible
  const values = Object.values(coordonnees).filter((v) => v);
  return values.length > 0 ? values[0] : null;
}

/**
 * Récupère l'URL du premier moyen de type SITE_WEB dans reservation.organismes.
 * Parcourt les organismes et leurs moyensCommunication ; dès qu'un SITE_WEB est trouvé, on le retourne.
 */
export function getReservationSiteWebUrl(
  item: ObjetTouristique,
  language: WidgetLanguage,
): string | null {
  const reservation = (item as any).reservation;
  const organismes = reservation?.organismes;
  if (!Array.isArray(organismes)) return null;
  for (const org of organismes) {
    const moyens = org.moyensCommunication;
    if (!Array.isArray(moyens)) continue;
    const siteWeb = moyens.find(
      (m: any) =>
        m.type?.enumKey === MoyenCommunicationType.SITE_WEB ||
        m.type?.id === 205,
    );
    if (!siteWeb) continue;
    const url = getCoordonneesValue(siteWeb.coordonnees, language);
    if (url) return url;
  }
  return null;
}

/**
 * Formate les périodes d'ouverture
 */
export function formatOuverture(
  periodes: PeriodeOuverture[] | undefined,
  params: ItemFieldUtilsParams,
): string | null {
  if (!periodes || periodes.length === 0) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Trouver la première période en cours ou future
  let periode: PeriodeOuverture | null = null;
  for (const p of periodes) {
    if (!p.dateDebut) continue;
    const dateDebut = new Date(p.dateDebut);
    // Si la période est en cours ou future
    if (dateDebut >= today) {
      periode = p;
      break;
    }
    // Si la période est en cours (dateDebut <= today <= dateFin)
    if (p.dateFin) {
      const dateFin = new Date(p.dateFin);
      if (dateDebut <= today && today <= dateFin) {
        periode = p;
        break;
      }
    }
  }

  // Si aucune période en cours/future, prendre la première
  if (!periode) {
    periode = periodes[0];
  }

  if (!periode.dateDebut) return null;

  const dateDebut = params.formatDate(periode.dateDebut, {
    day: "2-digit",
    month: "2-digit",
  });
  if (periode.dateFin) {
    if (isSameCalendarDay(periode.dateDebut, periode.dateFin)) {
      const periodOn = params.t("periodOn");
      const onLabel = periodOn === "periodOn" ? "Le" : periodOn;
      return onLabel ? `${onLabel} ${dateDebut}` : dateDebut;
    }
    const dateFin = params.formatDate(periode.dateFin, {
      day: "2-digit",
      month: "2-digit",
    });
    return `${params.t("periodFrom") || "Du"} ${dateDebut} ${params.t("periodTo") || "au"} ${dateFin}`;
  }
  return `${params.t("periodFrom") || "Du"} ${dateDebut}`;
}

/**
 * Récupère le nom de l'icône pour un champ
 */
export function getFieldIconName(fieldId: string): string {
  switch (fieldId) {
    case DisplayedFieldPreviewId.COMMUNE:
      return "bold/map_&_location/map_point";
    case DisplayedFieldPreviewId.OUVERTURE:
      return "bold/time/calendar_minimalistic";
    case DisplayedFieldPreviewId.TELEPHONE:
      return "bold/call/phone";
    case DisplayedFieldPreviewId.CATEGORIE:
      return "bold/money/tag";
    case DisplayedFieldPreviewId.RESERVATION:
      return "bold/messages_conversation/square_forward";
    default:
      return "•";
  }
}

/**
 * Récupère les données d'un champ pour un item
 */
export function getFieldData(
  item: ObjetTouristique,
  fieldId: string,
  params: ItemFieldUtilsParams,
): { value: string; url?: string } | null {
  switch (fieldId) {
    case DisplayedFieldPreviewId.COMMUNE:
      const commune = item.localisation?.adresse?.commune?.nom;
      return commune ? { value: commune } : null;

    case DisplayedFieldPreviewId.OUVERTURE:
      const ouverture = formatOuverture(
        item.ouverture?.periodesOuvertures,
        params,
      );
      return ouverture ? { value: ouverture } : null;

    case DisplayedFieldPreviewId.TELEPHONE:
      // Prendre le premier téléphone de la liste
      const telephone = item.informations?.moyensCommunication?.find(
        (m) =>
          m.type?.enumKey === MoyenCommunicationType.TELEPHONE ||
          m.type?.id === 201,
      );
      if (!telephone) return null;
      // Utiliser la locale pour obtenir les coordonnées
      const telValue = getCoordonneesValue(
        telephone.coordonnees,
        params.language,
      );
      return telValue ? { value: telValue } : null;

    case DisplayedFieldPreviewId.CATEGORIE:
      // Récupérer l'ElementReference du type depuis les structures d'informations
      const typeRef = getTypeElementReference(item);
      if (!typeRef) {
        return null;
      }
      // Utiliser FilterId.TYPES pour obtenir le libellé depuis les filtres
      const typeLabel = TranslationService.getLabelWithFallback(
        typeRef as LibelleMultilingue,
        params.language,
      );
      return typeLabel ? { value: typeLabel } : null;

    case DisplayedFieldPreviewId.RESERVATION:
      // URL du premier SITE_WEB dans reservation.organismes (parcours organismes → moyensCommunication)
      const reservationUrl = getReservationSiteWebUrl(item, params.language);
      return reservationUrl ? { value: "", url: reservationUrl } : null;

    default:
      return null;
  }
}
